import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../../components/HeroSection';
import { Compass, HelpCircle, Sprout, Accessibility, MessageCircle, Users, ArrowRight, Lightbulb, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';
export function HowWeWork() {
  const principles = [{
    icon: <Compass className="h-7 w-7" data-id="element-1463" />,
    title: 'We design with care and curiosity',
    description: 'Every learning experience begins with genuine questions. We listen before we build, and we stay curious throughout the process.'
  }, {
    icon: <HelpCircle className="h-7 w-7" data-id="element-1464" />,
    title: 'We question assumptions',
    description: 'We interrogate what we think we know — about learners, about fat oppression, about what "good" education looks like. Discomfort is part of the work.'
  }, {
    icon: <Sprout className="h-7 w-7" data-id="element-1465" />,
    title: 'We prioritise learning over perfection',
    description: 'We iterate, revise, and grow. We would rather release something honest and improve it than wait for something flawless that never arrives.'
  }, {
    icon: <Accessibility className="h-7 w-7" data-id="element-1466" />,
    title: 'We build things that are useful and accessible',
    description: 'Accessibility is not an afterthought. We design for diverse bodies, minds, and circumstances from the very beginning.'
  }, {
    icon: <MessageCircle className="h-7 w-7" data-id="element-1467" />,
    title: 'We stay accountable to our community',
    description: 'Feedback shapes our practice. When we get it wrong, we listen, acknowledge, and change. Accountability is ongoing, not performative.'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-1468">
      <HeroSection title="How We Work" subtitle="How we collaborate, make decisions, and approach the practice of building thoughtful learning." tag="About ReGrowth" bgColor="lime" textColor="navy" data-id="element-1469" />

      {/* Intro / Philosophy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1470">
        <div className="max-w-3xl mx-auto" data-id="element-1471">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1472">
            <p className="text-2xl text-gray-700 leading-relaxed mb-6" data-id="element-1473">
              ReGrowth is a small, collaborative studio. We design learning
              experiences rooted in fat liberation, social justice, and genuine
              care for the people who engage with our work.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-1474">
              We move thoughtfully. We ask questions before we build. We believe
              that how something is made matters as much as what it says — and
              that the best learning comes from honesty, not polish.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Subtle organic divider */}
      <div className="max-w-xs mx-auto px-4 opacity-20" data-id="element-1475">
        <svg viewBox="0 0 200 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-1476">
          <path d="M0 15 C50 5 150 25 200 15" stroke="#84CC16" strokeWidth="1.5" data-id="element-1477" />
          <circle cx="100" cy="15" r="3" fill="#84CC16" data-id="element-1478" />
        </svg>
      </div>

      {/* How We Work Together */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1479">
        <div className="max-w-5xl mx-auto" data-id="element-1480">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-1481">
            <div className="lg:col-span-2" data-id="element-1482">
              <Users className="h-10 w-10 text-brand-lime mb-4" data-id="element-1483" />
              <h2 className="text-3xl font-black uppercase text-brand-black mb-4 leading-tight" data-id="element-1484">
                How We Work Together
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-1485">
              <p data-id="element-1486">
                We are a small team, and that is intentional. Smallness allows
                us to be responsive, honest, and deeply involved in the work we
                produce.
              </p>
              <p data-id="element-1487">
                Collaboration at ReGrowth is built on{' '}
                <strong className="text-brand-black" data-id="element-1488">
                  open discussion and shared responsibility
                </strong>
                . We don't have rigid hierarchies. Instead, we respect the
                different expertise each person brings — whether that's subject
                matter knowledge, learning design, editorial craft, or technical
                implementation.
              </p>
              <p data-id="element-1489">
                We talk things through. We disagree respectfully. We make space
                for reflection, and we try not to rush decisions that deserve
                time.
              </p>
              <div className="bg-white border-l-4 border-brand-lime p-6 mt-8" data-id="element-1490">
                <p className="text-base text-gray-600 italic" data-id="element-1491">
                  "We work at the speed of trust, not the speed of output. The
                  best ideas come from listening, not from urgency."
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subtle organic divider */}
      <div className="max-w-xs mx-auto px-4 opacity-20" data-id="element-1492">
        <svg viewBox="0 0 200 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-1493">
          <path d="M0 15 C50 25 150 5 200 15" stroke="#84CC16" strokeWidth="1.5" data-id="element-1494" />
          <circle cx="60" cy="18" r="2" fill="#84CC16" data-id="element-1495" />
          <circle cx="140" cy="12" r="2" fill="#84CC16" data-id="element-1496" />
        </svg>
      </div>

      {/* Working Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1497">
        <div className="max-w-5xl mx-auto" data-id="element-1498">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12" data-id="element-1499">
            <h2 className="text-3xl font-black uppercase text-brand-black mb-4" data-id="element-1500">
              Our Working Principles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl" data-id="element-1501">
              These aren't abstract ideals. They are practical commitments that
              shape how we approach every project, conversation, and decision.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1502">
            {principles.map((principle, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className="bg-white border border-gray-200 p-8 flex flex-col" data-id="element-1503">
                <div className="text-brand-lime mb-5" data-id="element-1504">{principle.icon}</div>
                <h3 className="text-lg font-bold text-brand-black mb-3 leading-snug" data-id="element-1505">
                  {principle.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1506">
                  {principle.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How We Make Decisions */}
      <section className="py-20 bg-white" data-id="element-1507">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1508">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-1509">
            <div className="lg:col-span-2" data-id="element-1510">
              <Lightbulb className="h-10 w-10 text-brand-lime mb-4" data-id="element-1511" />
              <h2 className="text-3xl font-black uppercase text-brand-black mb-4 leading-tight" data-id="element-1512">
                How We Make Decisions
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-1513">
              <p data-id="element-1514">
                We don't have a complicated governance structure. We have
                conversations.
              </p>
              <p data-id="element-1515">
                When a decision needs to be made, we start with{' '}
                <strong className="text-brand-black" data-id="element-1516">open discussion</strong>.
                Everyone involved shares their perspective. We listen to the
                person with the most relevant expertise — whether that's lived
                experience, pedagogical knowledge, or technical understanding.
              </p>
              <p data-id="element-1517">
                When perspectives differ, we return to a simple question:{' '}
                <em data-id="element-1518">what serves the learner best?</em> The learner experience
                and the project's goals are our compass. Not ego, not
                convention, not what's fastest.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8" data-id="element-1519">
                <div className="border border-gray-200 p-5" data-id="element-1520">
                  <p className="text-sm font-bold uppercase tracking-wider text-brand-lime mb-2" data-id="element-1521">
                    First
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1522">
                    We discuss openly and listen to all perspectives.
                  </p>
                </div>
                <div className="border border-gray-200 p-5" data-id="element-1523">
                  <p className="text-sm font-bold uppercase tracking-wider text-brand-lime mb-2" data-id="element-1524">
                    Then
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1525">
                    We defer to relevant expertise and lived experience.
                  </p>
                </div>
                <div className="border border-gray-200 p-5" data-id="element-1526">
                  <p className="text-sm font-bold uppercase tracking-wider text-brand-lime mb-2" data-id="element-1527">
                    Always
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1528">
                    We prioritise the learner experience and project goals.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subtle organic divider */}
      <div className="max-w-xs mx-auto px-4 opacity-20 py-4" data-id="element-1529">
        <svg viewBox="0 0 200 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-1530">
          <path d="M0 15 C70 0 130 30 200 15" stroke="#84CC16" strokeWidth="1.5" data-id="element-1531" />
          <circle cx="100" cy="15" r="3" fill="#84CC16" data-id="element-1532" />
        </svg>
      </div>

      {/* How We Work With Partners */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-1533">
        <div className="max-w-5xl mx-auto" data-id="element-1534">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start" data-id="element-1535">
            <div className="lg:col-span-2" data-id="element-1536">
              <Handshake className="h-10 w-10 text-brand-lime mb-4" data-id="element-1537" />
              <h2 className="text-3xl font-black uppercase text-brand-black mb-4 leading-tight" data-id="element-1538">
                How We Work With Partners
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-1539">
              <p data-id="element-1540">
                If you're working with us — whether as a collaborator, a
                commissioning organisation, or a community partner — here's what
                you can expect.
              </p>

              <div className="space-y-8 mt-8" data-id="element-1541">
                <div className="flex gap-5" data-id="element-1542">
                  <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1543">
                    <span className="text-brand-lime font-bold text-sm" data-id="element-1544">
                      01
                    </span>
                  </div>
                  <div data-id="element-1545">
                    <h3 className="font-bold text-brand-black mb-1" data-id="element-1546">
                      Open communication
                    </h3>
                    <p className="text-base text-gray-600" data-id="element-1547">
                      We share our thinking, our process, and our uncertainties.
                      We'd rather be honest about a challenge than pretend
                      everything is seamless.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5" data-id="element-1548">
                  <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1549">
                    <span className="text-brand-lime font-bold text-sm" data-id="element-1550">
                      02
                    </span>
                  </div>
                  <div data-id="element-1551">
                    <h3 className="font-bold text-brand-black mb-1" data-id="element-1552">
                      Iterative design
                    </h3>
                    <p className="text-base text-gray-600" data-id="element-1553">
                      We build in stages, sharing work early and often. Feedback
                      is not a final step — it's woven into the entire process.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5" data-id="element-1554">
                  <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1555">
                    <span className="text-brand-lime font-bold text-sm" data-id="element-1556">
                      03
                    </span>
                  </div>
                  <div data-id="element-1557">
                    <h3 className="font-bold text-brand-black mb-1" data-id="element-1558">
                      Transparency
                    </h3>
                    <p className="text-base text-gray-600" data-id="element-1559">
                      We are upfront about timelines, capacity, and costs. If
                      something isn't working, we'll say so — and we expect the
                      same in return.
                    </p>
                  </div>
                </div>

                <div className="flex gap-5" data-id="element-1560">
                  <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1561">
                    <span className="text-brand-lime font-bold text-sm" data-id="element-1562">
                      04
                    </span>
                  </div>
                  <div data-id="element-1563">
                    <h3 className="font-bold text-brand-black mb-1" data-id="element-1564">
                      Shared ownership
                    </h3>
                    <p className="text-base text-gray-600" data-id="element-1565">
                      The best outcomes come from genuine partnership. We bring
                      our expertise, you bring yours, and the work belongs to
                      both of us.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-24 bg-brand-lime/10" data-id="element-1566">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1567">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1568">
            {/* Subtle illustration */}
            <div className="mx-auto mb-8 opacity-30 w-24" data-id="element-1569">
              <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1570">
                <path d="M50 90 C50 50 30 30 50 10" stroke="#84CC16" strokeWidth="2" data-id="element-1571" />
                <path d="M50 60 C60 50 70 55 75 45" stroke="#84CC16" strokeWidth="1.5" data-id="element-1572" />
                <path d="M50 70 C40 60 30 65 25 55" stroke="#84CC16" strokeWidth="1.5" data-id="element-1573" />
                <circle cx="50" cy="10" r="4" fill="#84CC16" data-id="element-1574" />
                <circle cx="75" cy="45" r="3" fill="#84CC16" data-id="element-1575" />
                <circle cx="25" cy="55" r="3" fill="#84CC16" data-id="element-1576" />
              </svg>
            </div>

            <p className="text-2xl text-gray-700 leading-relaxed mb-6" data-id="element-1577">
              We are building something slowly and with intention. Not because
              slow is always better, but because the work we do — learning about
              oppression, unlearning bias, growing toward justice — deserves
              time, care, and honesty.
            </p>
            <p className="text-lg text-gray-500 leading-relaxed mb-10" data-id="element-1578">
              If that resonates with you, we'd love to hear from you.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-brand-navy bg-brand-lime hover:bg-brand-lime/90 transition-all uppercase tracking-wide" data-id="element-1579">
              Get in Touch <ArrowRight className="ml-2 h-4 w-4" data-id="element-1580" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}